// Batch 0 (UX/IA overhaul) — pure grouping for the camera picker.
//
// The flat, ungrouped camera <select> (Layouts, Recordings, Analytics, Zones,
// Fire) doesn't scale and hides the hierarchy. This groups a CameraRef[] by
// NVR (default) or Site so <CameraPicker> can render Radix SelectGroups with a
// heading per NVR/site. Cameras with no known parent fall into an "Ungrouped"
// bucket, which always sorts last. Pure + sorted so the picker order is stable
// and testable.
import type { CameraRef } from "@/types/deviceRef";

export interface CameraGroup<T extends CameraRef = CameraRef> {
  key: string;
  label: string;
  cameras: T[];
}

const UNGROUPED = "Ungrouped";

/** T3c — label a group by its NVR SERIAL when make/model are unknown.
 *
 * Every one of the 15 live cameras carries `nvr_serial` (measured on prod
 * 2026-08-06: 15/15) and NONE carries `nvr_make` or `nvr_model` (0/15) — the
 * make/model live in P1B `camera_logical` and the projection that would copy
 * them onto the flat row has never run (ADR-005).
 *
 * So the grouping was never broken: `key` already prefers `nvr_serial`, and
 * the cameras WERE bucketed one group per NVR. Only the LABEL fell through to
 * "Ungrouped" — every bucket rendered under the same heading, which read as
 * "these cameras have no NVR" when in fact each group knew exactly which NVR
 * it was. A known identity displayed as an unknown one is the same false-fact
 * class as the rest of this tranche.
 *
 * Naming the serial is honest and available today. It does NOT substitute for
 * projecting make/model onto the flat row — when that lands this falls back to
 * the nicer label on its own, with no change here.
 */
export function nvrGroupLabel(cam: CameraRef): string {
  const parts = [cam.nvr_make, cam.nvr_model]
    .map((s) => (s ?? "").trim())
    .filter(Boolean);
  if (parts.length) return parts.join(" ");
  const serial = (cam.nvr_serial ?? "").trim();
  if (serial) return `NVR ${serial.slice(-8)}`;
  return UNGROUPED;
}

function nvrLabel(cam: CameraRef): string {
  return nvrGroupLabel(cam);
}

// Generic over the element type so callers passing a richer camera object
// (e.g. the FE `Camera`) get groups whose `cameras` keep that type — not
// widened back to CameraRef.
export function groupCameras<T extends CameraRef>(
  cameras: T[],
  by: "nvr" | "site" = "nvr",
): CameraGroup<T>[] {
  const buckets = new Map<string, CameraGroup<T>>();

  for (const cam of cameras) {
    const label =
      by === "site" ? (cam.site_name ?? "").trim() || UNGROUPED : nvrLabel(cam);
    // Group key: prefer a stable id so two NVRs sharing a make/model label
    // don't merge. For NVR grouping that's source_id when present (dormant
    // today) then nvr_serial (the real per-NVR key on the cameras row); for
    // site grouping it's site_id. Fall back to the label.
    const key =
      (by === "site" ? cam.site_id : cam.source_id || cam.nvr_serial) || label;
    let group = buckets.get(key);
    if (!group) {
      group = { key, label, cameras: [] };
      buckets.set(key, group);
    }
    group.cameras.push(cam);
  }

  const groups = Array.from(buckets.values());
  // Sort groups alphabetically, Ungrouped always last; cameras by name.
  groups.sort((a, b) => {
    // Both-ungrouped must compare EQUAL. `if (a.label === UNGROUPED) return 1`
    // alone answered 1 for (a,b) AND for (b,a) once more than one group
    // carried the label — an inconsistent comparator, and with make/model
    // NULL fleet-wide EVERY group carried it. Ordering was implementation-
    // defined; now it is not.
    const aUn = a.label === UNGROUPED;
    const bUn = b.label === UNGROUPED;
    if (aUn && bUn) return 0;
    if (aUn) return 1;
    if (bUn) return -1;
    return a.label.localeCompare(b.label);
  });
  for (const g of groups) {
    g.cameras.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }
  return groups;
}
